import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { Check, ShieldCheck, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { StatusChip } from '@/design';
import { TEAM_PATHS, useAdminData, userPath } from '@/domains/admin';
import { useCan } from '@/domains/auth';
import { editorsApi, type IContentEditor } from '../api/editors-api';
import { EmptyNote } from '../shared';
import { ContentPage } from './content-page';

/**
 * Who can publish (WEB-13 AC4).
 *
 * **This screen used to be the acceptance criterion; now it only explains it.** AC4 asks that
 * Veritant be able to grant publishing to as many or as few named people as they choose, without a
 * code change. That is answered by two real permission codes — `content.author` and
 * `content.publish` — held by roles, which are rows edited on the Setup screen without a release.
 * Where the answer lives is the roles screen, so this one points at it rather than running a
 * second, simulated model beside it.
 *
 * **It drew a roster of invented people with two toggles.** Both are gone. A permission that can
 * be switched on a mock screen, beside one that cannot, teaches whoever is signing the screen off
 * the wrong model — and the toggles drove nothing once the post editor started reading
 * `/auth/me`.
 *
 * What is left is the three things a content editor standing here actually wants to know: what
 * the split means, which half of it they hold, and who else holds either — survey-api's
 * `/admin/website/editors`, readable with `content.author` alone.
 */
export function ContentAccessPage() {
  const can = useCan();
  const author = can('content.author');
  const publish = can('content.publish');
  const { data: editors, error, reload } = useAdminData<IContentEditor[]>((signal) =>
    editorsApi.list(signal)
  );

  return (
    <ContentPage
      title="Who can publish"
      lede="Writing and publishing are separate permissions, granted by role."
    >
      <Alert>
        <ShieldCheck className="h-4 w-4" aria-hidden />
        <AlertTitle>Two permissions, granted on the roles screen</AlertTitle>
        <AlertDescription>
          <code>content.author</code> opens these screens and saves a post;{' '}
          <code>content.publish</code> is what puts one on the live site. Neither is a setting in
          this section — both are held by Team Portal roles, which are rows an administrator edits
          without a release. Widening or narrowing who publishes is a role edit.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">What you hold</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          <Held held={author} label="Write and edit posts" code="content.author" />
          <Held held={publish} label="Put a post on the live site" code="content.publish" />
          <p className="text-xs text-muted-foreground">
            As the server answered it on this session&apos;s last read. A role change reaches the
            next request, not this page.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Who can edit the website{editors ? ` (${editors.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2">
          {error && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-destructive">
              {error}
              <Button size="sm" variant="outline" onClick={() => void reload()}>
                Try again
              </Button>
            </div>
          )}
          {editors?.length === 0 && (
            <EmptyNote>No active account holds either permission.</EmptyNote>
          )}
          {editors?.map((editor) => (
            <Editor key={editor.accountId} editor={editor} linked={can('accounts.browse')} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Why they are separate</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm">
          <p>
            Whoever holds <code>content.publish</code> publishes immediately — there is no approval
            queue behind the button, which is the uploaded requirements&apos; position. Limiting
            who holds it is the 11 September call&apos;s. Both are satisfied by this being a
            permission rather than a workflow step.
          </p>
          <p className="text-muted-foreground">
            An author without it writes and saves freely, and marks a post finished when they are
            done. That stamps the post and gates nothing: there is no state a post sits in waiting
            for somebody.
          </p>
          <div>
            <Button asChild size="sm" variant="outline">
              <Link to={TEAM_PATHS.roles}>Open the roles screen</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </ContentPage>
  );
}

/** One account and what it may do. The address links to the account's card only for a viewer who
 *  can open it. */
function Editor({ editor, linked }: { editor: IContentEditor; linked: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border py-2 last:border-b-0">
      <span className="grid min-w-0 flex-1 gap-0.5">
        {linked ? (
          <Link
            to={userPath(editor.email)}
            className="truncate text-sm font-medium text-[var(--color-sapphire)] hover:underline"
          >
            {editor.email}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium">{editor.email}</span>
        )}
        <span className="truncate text-xs text-muted-foreground">{editor.roles.join(', ')}</span>
      </span>
      {editor.author && <StatusChip tone="neutral">Writes</StatusChip>}
      {editor.publish && <StatusChip tone="success">Publishes</StatusChip>}
    </div>
  );
}

function Held({ held, label, code }: { held: boolean; label: string; code: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border py-2 last:border-b-0">
      {held ? (
        <Check className="h-4 w-4 text-[var(--color-teal)]" aria-hidden />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" aria-hidden />
      )}
      <span className="min-w-0 flex-1 text-sm">{label}</span>
      <code className="text-xs text-muted-foreground">{code}</code>
      <StatusChip tone={held ? 'success' : 'neutral'}>{held ? 'Yes' : 'No'}</StatusChip>
    </div>
  );
}
